import "./ClaimNumberInput.scss";

import cx from "classnames";
import { DatePicker, TextInput } from "components/inputs";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import type { FieldErrors } from "react-hook-form";
import { useParams } from "react-router-dom";
import { DATE_TO_STRING, STRING_TO_DATE } from "utils/date-utils";

export type DateOfInjuryType = {
  id?: number;
  date: string;
  organization?: string;
  file: number;
  tempId?: string;
};

export type ClaimNumberType = {
  id?: number;
  organization?: string;
  file: number;
  date_of_injury_id: number;
  number: string;
  errors?: FieldErrors;
};

export type MultiClaimDateInputReadOnlyProps = {
  dateOfInjury: DateOfInjuryType;
  initialClaimNumbers: ClaimNumberType[];
  canDeleteDoi: boolean;
  canAddDoi: boolean;
  onAddDoi: (date: Date | null) => void;
  onDeleteDoi: (doiId: number | undefined, tempId?: string) => void;
  updateState: (
    doiId: number | undefined,
    tempId: string | undefined,
    date: string,
    claimNumbers: { number: string; id?: number }[],
  ) => void;
  errors?: FieldErrors;
};

const ActionButtons = ({
  onAdd,
  onDelete,
  canAdd = true,
  canDelete = false,
}) => (
  <figure className="buttons-container-add-delete">
    <div className="flex jc-end">
      {canAdd && (
        <button
          type="button"
          onClick={onAdd}
          className="btn add"
          aria-label="Add Field"
        >
          <span className="material-icons">add</span>
        </button>
      )}
      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="btn delete"
          aria-label="Delete Field"
        >
          <span className="material-icons">delete</span>
        </button>
      )}
    </div>
  </figure>
);

const MultiClaimDateInputReadOnly = ({
  dateOfInjury,
  initialClaimNumbers,
  canDeleteDoi,
  onAddDoi,
  canAddDoi,
  updateState,
  onDeleteDoi,
  errors,
}: MultiClaimDateInputReadOnlyProps) => {
  const [claimNumbers, setClaimNumbers] = useState<
    { number: string; id?: number }[]
  >([{ number: "" }]);
  const [currentDate, setCurrentDate] = useState(dateOfInjury.date || "");

  useEffect(() => {
    if (dateOfInjury.id !== undefined) {
      const claimNumbersByDoi = initialClaimNumbers
        .filter((cn) => cn.date_of_injury_id === dateOfInjury.id)
        .map((cn) => ({ number: cn.number, id: cn.id }));

      if (claimNumbersByDoi?.length) {
        setClaimNumbers(claimNumbersByDoi);
      } else {
        setClaimNumbers([{ number: "" }]);
      }
    } else if (!dateOfInjury.id && claimNumbers.length === 0) {
      setClaimNumbers([{ number: "" }]);
    }
  }, [initialClaimNumbers, dateOfInjury.id]);

  useEffect(() => {
    if (dateOfInjury.date && dateOfInjury.date !== currentDate) {
      setCurrentDate(dateOfInjury.date);
    }
  }, [dateOfInjury.date]);

  const debouncedUpdateState = useCallback(
    debounce((date: string, numbers: typeof claimNumbers) => {
      const claimNumbersToPass = numbers
        .filter((cn) => cn.number && cn.number.trim() !== "")
        .map((cn) => ({ number: cn.number, id: cn.id }));

      updateState(
        dateOfInjury.id,
        dateOfInjury.tempId,
        date,
        claimNumbersToPass,
      );
    }, 300),
    [dateOfInjury.id, dateOfInjury.tempId, updateState],
  );

  useEffect(() => {
    debouncedUpdateState(currentDate, claimNumbers);

    return () => {
      debouncedUpdateState.cancel();
    };
  }, [claimNumbers, currentDate, debouncedUpdateState]);

  const handleDateChange = (date: Date | null) => {
    const dateString = DATE_TO_STRING(date);
    setCurrentDate(dateString);
  };

  const addClaimNumber = () => {
    setClaimNumbers((prev) => [...prev, { number: "" }]);
  };

  const updateClaimNumber = (index: number, value: string) => {
    setClaimNumbers((prev) => {
      const newValues = [...prev];
      if (newValues[index]) {
        newValues[index] = {
          ...newValues[index],
          number: value,
        };
      } else {
        newValues[index] = { number: value };
      }
      return newValues;
    });
  };

  const deleteClaimNumber = (index: number) => {
    setClaimNumbers((prev) => {
      const newValues = prev.filter((_, i) => i !== index);
      return newValues.length > 0 ? newValues : [{ number: "" }];
    });
  };

  return (
    <div className="multi-date-picker-container">
      <div className="multi-row-adjust">
        <label>Date of Injury</label>
        <div className={cx("input-container")}>
          <DatePicker
            errors={errors}
            name={`doi_${dateOfInjury.id || dateOfInjury.tempId}`}
            initialDate={STRING_TO_DATE(currentDate)}
            className={
              canAddDoi || canDeleteDoi ? "w-two-btns" : "datepicker--custom"
            }
            onDateChange={handleDateChange}
          />
          <ActionButtons
            onAdd={() => onAddDoi(null)}
            onDelete={() => onDeleteDoi(dateOfInjury.id, dateOfInjury.tempId)}
            canAdd={canAddDoi}
            canDelete={canDeleteDoi}
          />
        </div>

        {claimNumbers.map((claimNumber, index) => (
          <div
            className="multi-row-adjust"
            key={`claim-${dateOfInjury.id || dateOfInjury.tempId}-${index}`}
          >
            <label>{`Claim Number ${index + 1}`}</label>
            <div className={cx("input-container")}>
              <TextInput
                name={`claim_${dateOfInjury.id || dateOfInjury.tempId}_${index}`}
                value={claimNumber?.number || ""}
                readOnly={false}
                errors={errors}
                onChange={(e) => {
                  updateClaimNumber(index, e.target.value);
                }}
              />
              <ActionButtons
                onAdd={addClaimNumber}
                onDelete={() => deleteClaimNumber(index)}
                canAdd={index === claimNumbers.length - 1}
                canDelete={
                  claimNumbers.length > 1 ||
                  (claimNumbers.length === 1 && claimNumbers[0].number !== "")
                }
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const DateOfInjuryForm = ({
  initialClaimNumbers,
  initialDatesOfInjury = [],
  onChange,
  errors,
}: {
  errors?: FieldErrors;
  initialClaimNumbers: ClaimNumberType[];
  initialDatesOfInjury?: DateOfInjuryType[];
  onChange: (
    data: Array<{
      date: string;
      claim_numbers: Array<{ number: string; id?: number }>;
    }>,
  ) => void;
}) => {
  const { fileId } = useParams<{ fileId: string }>();
  const [dates, setDates] = useState<DateOfInjuryType[]>([]);
  const [stateData, setStateData] = useState<
    {
      doi_id?: number;
      tempId?: string;
      date: string;
      claim_numbers: { number: string; id?: number }[];
    }[]
  >([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized) {
      let initialInternalState: typeof stateData = [];
      let initialDatesState: DateOfInjuryType[] = [];

      if (initialDatesOfInjury?.length) {
        initialDatesState = initialDatesOfInjury.map((doi, index) => ({
          ...doi,
          tempId: doi.tempId || `existing-${doi.id || Date.now()}-${index}`,
        }));

        initialInternalState = initialDatesState.map((doi) => {
          const doiClaimNumbers = initialClaimNumbers
            .filter((cn) => cn.date_of_injury_id === doi.id)
            .map((cn) => ({ number: cn.number, id: cn.id }));

          return {
            doi_id: doi.id,
            tempId: doi.tempId,
            date: doi.date,
            claim_numbers: doiClaimNumbers.length ? doiClaimNumbers : [],
          };
        });
      } else {
        const tempId = `new-${Date.now()}-0`;
        const emptyDate: DateOfInjuryType = {
          date: "",
          file: Number(fileId),
          tempId,
        };
        initialDatesState = [emptyDate];
        initialInternalState = [{ tempId, date: "", claim_numbers: [] }];
      }

      setDates(initialDatesState);
      setStateData(initialInternalState);
      setInitialized(true);
    }
  }, [initialDatesOfInjury, initialClaimNumbers, fileId, initialized]);

  const formatDataForParent = useCallback((data: typeof stateData) => {
    return data
      .filter(
        (item) =>
          item.date ||
          item.claim_numbers.some((cn) => cn.number && cn.number.trim() !== ""),
      )
      .map((item) => ({
        date: item.date,
        claim_numbers: (item.claim_numbers || [])
          .filter((cn) => cn.number && cn.number.trim() !== "")
          .map((cn) => ({ number: cn.number, id: cn.id })),
      }));
  }, []);

  const updateStateData = useCallback(
    (
      doiId: number | undefined,
      tempId: string | undefined,
      date: string,
      claimNumbers: { number: string; id?: number }[],
    ) => {
      setStateData((prev) => {
        const identifier = doiId !== undefined ? "doi_id" : "tempId";
        const value = doiId !== undefined ? doiId : tempId;

        const existingIndex = prev.findIndex(
          (item) => item[identifier] === value,
        );

        const updatedItem = {
          doi_id: doiId,
          tempId: tempId,
          date: date,
          claim_numbers: claimNumbers,
        };

        let newStateData;
        if (existingIndex !== -1) {
          newStateData = [...prev];
          newStateData[existingIndex] = updatedItem;
        } else if (tempId) {
          newStateData = [...prev, updatedItem];
        } else {
          console.warn("Could not find item to update and no tempId provided", {
            doiId,
            tempId,
            date,
            claimNumbers,
          });
          newStateData = [...prev];
        }

        const formattedData = formatDataForParent(newStateData);
        onChange(formattedData);

        return newStateData;
      });

      setDates((prevDates) => {
        const identifier = doiId !== undefined ? "id" : "tempId";
        const value = doiId !== undefined ? doiId : tempId;
        const dateIndex = prevDates.findIndex((d) => d[identifier] === value);

        if (dateIndex !== -1 && prevDates[dateIndex].date !== date) {
          const newDates = [...prevDates];
          newDates[dateIndex] = { ...newDates[dateIndex], date: date };
          return newDates;
        }
        return prevDates;
      });
    },
    [formatDataForParent, onChange],
  );

  const handleAddDateOfInjury = () => {
    const tempId = `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newDate: DateOfInjuryType = {
      date: "",
      file: Number(fileId),
      tempId,
    };

    setDates((prevDates) => [...prevDates, newDate]);

    setStateData((prev) => [...prev, { tempId, date: "", claim_numbers: [] }]);
  };

  const handleDeleteDoi = (doiId: number | undefined, tempId?: string) => {
    if (dates.length <= 1) return;

    const identifier = doiId !== undefined ? "id" : "tempId";
    const value = doiId !== undefined ? doiId : tempId;

    const newDates = dates.filter((date) => date[identifier] !== value);
    setDates(newDates);

    const newStateData = stateData.filter((item) => {
      const itemValue = item.doi_id !== undefined ? item.doi_id : item.tempId;
      return itemValue !== value;
    });
    setStateData(newStateData);

    const formattedData = formatDataForParent(newStateData);
    onChange(formattedData);
  };

  // Function to map nested errors to the format expected by DatePicker and TextInput
  const getErrorsForDoi = (
    doiId: number | undefined,
    tempId: string | undefined,
  ) => {
    if (!errors) return undefined;

    const mappedErrors: Record<string, { type: string; message: string }> = {};

    // Handle top-level array validation error (if empty array)
    if (errors.dates_of_injury && !Array.isArray(errors.dates_of_injury)) {
      // Add error to the first date input if there's a global validation error on the array
      mappedErrors[`doi_${doiId || tempId}`] = {
        type: "required",
        message: String(
          errors.dates_of_injury.message ||
            "At least one date of injury is required",
        ),
      };

      // Also add error to first claim number if it exists
      mappedErrors[`claim_${doiId || tempId}_0`] = {
        type: "required",
        message: "Claim number is required",
      };

      return mappedErrors;
    }

    // Handle errors for specific entries if the array isn't empty
    if (!errors.dates_of_injury || !Array.isArray(errors.dates_of_injury))
      return undefined;

    // Find the index of this DOI in the dates_of_injury array
    const doiIndex = dates.findIndex(
      (d) =>
        (doiId !== undefined && d.id === doiId) ||
        (tempId !== undefined && d.tempId === tempId),
    );

    if (doiIndex === -1) return undefined;

    // Map date errors
    if (errors.dates_of_injury[doiIndex]?.date) {
      mappedErrors[`doi_${doiId || tempId}`] = {
        type: "required",
        message: String(errors.dates_of_injury[doiIndex].date.message),
      };
    }

    // Handle array-level validation for claim_numbers
    if (
      errors.dates_of_injury[doiIndex]?.claim_numbers &&
      !Array.isArray(errors.dates_of_injury[doiIndex]?.claim_numbers)
    ) {
      mappedErrors[`claim_${doiId || tempId}_0`] = {
        type: "required",
        message: String(
          errors.dates_of_injury[doiIndex].claim_numbers.message ||
            "At least one claim number is required",
        ),
      };
    }

    // Map individual claim number errors
    const claimErrors = errors.dates_of_injury[doiIndex]?.claim_numbers;
    if (claimErrors && Array.isArray(claimErrors)) {
      claimErrors.forEach((claimError, claimIndex) => {
        if (claimError?.number) {
          mappedErrors[`claim_${doiId || tempId}_${claimIndex}`] = {
            type: "required",
            message: String(claimError.number.message),
          };
        }
      });
    }

    return Object.keys(mappedErrors).length > 0 ? mappedErrors : undefined;
  };

  return (
    <>
      {dates.map((dateOfInjury, index) => (
        <MultiClaimDateInputReadOnly
          key={`doi-${dateOfInjury.id || dateOfInjury.tempId}`}
          canDeleteDoi={dates.length > 1}
          errors={getErrorsForDoi(dateOfInjury.id, dateOfInjury.tempId)}
          canAddDoi={index === dates.length - 1}
          onAddDoi={handleAddDateOfInjury}
          onDeleteDoi={handleDeleteDoi}
          dateOfInjury={dateOfInjury}
          initialClaimNumbers={initialClaimNumbers}
          updateState={updateStateData}
        />
      ))}
    </>
  );
};

export default DateOfInjuryForm;
